import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { Activity as ActivityIcon, Bot, ExternalLink, Inbox, MessagesSquare, Radar, UserX } from 'lucide-react';
import { AdminAPI } from '@shared/types/api';
import { useAdminOpsOverview, useOpsSweep } from '../api/useAdminOps';
import StageRunButtons from '../components/ops/StageRunButtons';
import BulkStageRunControl from '../components/ops/BulkStageRunControl';
import EventStageChips from '../components/ops/EventStageChips';
import RunDetailDrawer from '../components/ops/RunDetailDrawer';
import {
  HealthDot,
  RefreshButton,
  StagePips,
  TASK_LABELS,
  formatLocalDinner,
  tableSizeList,
  timeAgo,
  timeUntil,
} from '../components/ops/opsUi';

// Ops overview: a status grid with one small tile per (event × neighborhood),
// grouped by event date, worst health first. Each tile shows the scope's
// pipeline as stage pips; detail lives in the hover card and the detail page.

function countdown(localDinnerAt: string): string {
  const hours = (new Date(localDinnerAt).getTime() - Date.now()) / 3600000;
  if (hours < 0) return 'past';
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

function formatDateHeader(date: string): string {
  try {
    return new Date(`${date}T12:00:00`).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return date;
  }
}

const HEALTH_RANK: Record<AdminAPI.OpsHealth, number> = { green: 0, yellow: 1, red: 2 };

const RUN_DOT: Record<string, string> = {
  completed: 'text-emerald-500',
  running: 'text-blue-500 animate-pulse',
  queued: 'text-blue-400',
  failed: 'text-red-500',
  cancelled: 'text-neutral-400',
};

interface ScopeEntry {
  tile: AdminAPI.OpsTile;
  n: AdminAPI.OpsNeighborhoodStats;
}

function ScopeHoverCard({ tile, n }: ScopeEntry) {
  const [, navigate] = useLocation();

  return (
    <div
      className="absolute left-0 right-0 top-full mt-1 z-30 min-w-[19rem] rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-xl p-3 space-y-2"
      onClick={(e) => e.stopPropagation()}
    >
      <p className="text-xs text-dark-muted">
        {formatLocalDinner(tile)}
        {n.nextMilestone && (
          <>
            {' · next: '}
            {TASK_LABELS[n.nextMilestone.task] ?? n.nextMilestone.task}{' '}
            {timeUntil(n.nextMilestone.plannedAt)}
          </>
        )}
        {!n.automationEnabled && ' · automation off'}
      </p>

      {n.healthReason && (
        <p
          className={`text-xs font-medium ${n.health === 'red' ? 'text-red-600' : 'text-amber-600'}`}
        >
          {n.healthReason}
        </p>
      )}

      <div className="text-xs space-y-0.5">
        <p>
          <span className="font-semibold">{n.booked}</span> booked ·{' '}
          <span className="font-semibold">{n.seated}</span> seated
          {n.unassigned > 0 && (
            <span className="text-red-600 font-semibold"> · {n.unassigned} unassigned</span>
          )}
        </p>
        <p className="text-dark-muted">
          tables {tableSizeList(n.tableSizes)}
          {n.lockedTables > 0 && ` (${n.lockedTables} locked)`}
          {tile.eventType !== 'coffee' &&
            ` · reserved ${n.reserved}/${n.reserved + n.reservationsNeeded}`}
        </p>
        {n.lastRun && (
          <p className="text-dark-muted">
            last run: {TASK_LABELS[n.lastRun.taskType] ?? n.lastRun.taskType} — {n.lastRun.status}{' '}
            {timeAgo(n.lastRun.finishedAt)}
            {n.lastRun.costUsd != null && ` · $${n.lastRun.costUsd.toFixed(2)}`}
          </p>
        )}
        {n.pendingDecisions > 0 && (
          <p className="text-amber-600 font-medium">
            {n.pendingDecisions} pending decision{n.pendingDecisions > 1 ? 's' : ''}
          </p>
        )}
      </div>

      <div className="pt-1 border-t border-neutral-100 dark:border-neutral-800">
        <p className="text-[10px] text-dark-muted mb-1">Pipeline — click a stage to run it:</p>
        <StageRunButtons
          eventTimeId={tile.eventTimeId}
          neighborhoodId={n.neighborhoodId}
          milestones={n.milestones}
        />
      </div>

      <div className="flex items-center gap-2 pt-1 border-t border-neutral-100 dark:border-neutral-800">
        <button
          onClick={() => navigate(`/ops/${tile.eventTimeId}/${encodeURIComponent(n.neighborhoodId)}`)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800"
        >
          Ops detail
        </button>
        <button
          onClick={() =>
            navigate(
              `/matching/${tile.eventTimeId}?neighborhood=${encodeURIComponent(n.neighborhoodName)}`
            )
          }
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800"
        >
          <ExternalLink className="w-3 h-3" /> Matching
        </button>
      </div>
    </div>
  );
}

// Per-event change sweep: queue a re-optimize for every neighborhood of THIS
// event (inside the T-36h…T-12h window) whose seating changed since its last
// run. Same logic the watcher runs hourly across all events — this button is
// the on-demand version, one event at a time.
function EventSweepButton({ eventTimeId }: { eventTimeId: string }) {
  const sweep = useOpsSweep();
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <button
        onClick={() => sweep.mutate(eventTimeId)}
        disabled={sweep.isPending}
        title="Change sweep for this event: queue a re-optimize for every neighborhood whose seating changed since its last run (works anytime — the T-36h…T-12h window only paces the automatic hourly sweep)"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-neutral-200 dark:border-neutral-700 text-[10px] font-medium text-dark-muted hover:border-primary-400 hover:text-primary-500 disabled:opacity-50"
      >
        <Radar className={`w-3 h-3 ${sweep.isPending ? 'animate-pulse' : ''}`} />
        Sweep
      </button>
      {sweep.data?.message && (
        <span className="text-[10px] text-dark-muted">{sweep.data.message}</span>
      )}
      {sweep.isError && (
        <span className="text-[10px] text-red-600">
          {(sweep.error as Error)?.message ?? 'sweep failed'}
        </span>
      )}
    </span>
  );
}

// The one currently-open hover card's close function — lets a newly hovered
// tile close the previous card immediately instead of waiting out its grace
// timer (only one card should ever be visible).
let closeActiveHoverCard: (() => void) | null = null;

function ScopeTile({ tile, n }: ScopeEntry) {
  const [, navigate] = useLocation();

  // Hover card with a close grace period: leaving the tile keeps the card
  // open briefly so the pointer can travel across the gap into it. Hovering
  // a DIFFERENT tile closes the previous card instantly (see
  // closeActiveHoverCard) — the grace only applies when leaving into space.
  const [cardOpen, setCardOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeSelf = useRef(() => setCardOpen(false)).current;
  const openCard = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (closeActiveHoverCard && closeActiveHoverCard !== closeSelf) closeActiveHoverCard();
    closeActiveHoverCard = closeSelf;
    setCardOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setCardOpen(false);
      if (closeActiveHoverCard === closeSelf) closeActiveHoverCard = null;
    }, 350);
  };
  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (closeActiveHoverCard === closeSelf) closeActiveHoverCard = null;
  }, [closeSelf]);

  const borderColor =
    n.health === 'red'
      ? 'border-red-400 dark:border-red-600'
      : n.health === 'yellow'
        ? 'border-amber-300 dark:border-amber-600'
        : 'border-neutral-200 dark:border-neutral-700';

  return (
    <div className="relative" onMouseEnter={openCard} onMouseLeave={scheduleClose}>
      <button
        onClick={() => navigate(`/ops/${tile.eventTimeId}/${encodeURIComponent(n.neighborhoodId)}`)}
        className={`w-full text-left rounded-lg border ${borderColor} bg-white dark:bg-neutral-900 px-2.5 py-1.5 hover:shadow-md transition-shadow`}
      >
        <div className="flex items-center gap-1.5">
          <span title={n.healthReason ?? undefined}>
            <HealthDot health={n.health} />
          </span>
          <span className="font-semibold text-xs truncate">{n.neighborhoodName}</span>
          <span className="text-[10px] text-dark-muted truncate">{tile.cityName}</span>
          <span className="ml-auto inline-flex items-center gap-1.5 shrink-0">
            {n.pendingDecisions > 0 && (
              <span
                className="inline-flex items-center gap-0.5 text-amber-600"
                title={`${n.pendingDecisions} pending decision(s)`}
              >
                <Inbox className="w-3 h-3" />
                <span className="text-[10px] font-bold">{n.pendingDecisions}</span>
              </span>
            )}
            {n.lastRun && (
              <span
                title={`Last run: ${TASK_LABELS[n.lastRun.taskType] ?? n.lastRun.taskType} — ${n.lastRun.status} ${timeAgo(n.lastRun.finishedAt)}`}
              >
                <Bot className={`w-3 h-3 ${RUN_DOT[n.lastRun.status] ?? 'text-dark-muted'}`} />
              </span>
            )}
            <span className="text-[10px] text-dark-muted tabular-nums">
              {countdown(tile.localDinnerAt)}
            </span>
          </span>
        </div>

        <div className="mt-1 flex items-center gap-1.5 text-[11px] leading-4">
          <StagePips milestones={n.milestones} />
          <span
            className="ml-auto tabular-nums text-dark-muted shrink-0"
            title={`${n.seated} seated of ${n.booked} booked`}
          >
            {n.seated}/{n.booked}
          </span>
          {n.unassigned > 0 && (
            <span
              className="text-red-600 font-semibold shrink-0"
              title={`${n.unassigned} unassigned`}
            >
              +{n.unassigned}
            </span>
          )}
        </div>
      </button>
      {cardOpen && <ScopeHoverCard tile={tile} n={n} />}
    </div>
  );
}

export default function AdminOps() {
  const { data, isLoading, error, isFetching, refetch } = useAdminOpsOverview();
  const tiles = data?.data?.tiles ?? [];
  const pendingTotal = data?.data?.pendingDecisionsTotal ?? 0;
  const [showAll, setShowAll] = useState(false);
  // Drawer for event-level stage reports (the audit snapshot).
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  // One entry per (event × neighborhood), grouped by date, then by event —
  // a date can hold more than one event (e.g. brunch + dinner), and the bulk
  // "run this stage for all neighborhoods" action is scoped per event.
  const byDate = new Map<string, Map<string, ScopeEntry[]>>();
  for (const tile of tiles) {
    let byEvent = byDate.get(tile.date);
    if (!byEvent) {
      byEvent = new Map();
      byDate.set(tile.date, byEvent);
    }
    const list = byEvent.get(tile.eventTimeId) ?? [];
    for (const n of tile.neighborhoods) {
      list.push({ tile, n });
    }
    byEvent.set(tile.eventTimeId, list);
  }
  for (const byEvent of byDate.values()) {
    for (const list of byEvent.values()) {
      list.sort(
        (a, b) =>
          a.tile.cityName.localeCompare(b.tile.cityName) ||
          HEALTH_RANK[b.n.health] - HEALTH_RANK[a.n.health] ||
          a.n.neighborhoodName.localeCompare(b.n.neighborhoodName)
      );
    }
  }
  const dates = [...byDate.keys()].sort();
  const visibleDates = showAll ? dates : dates.slice(0, 3);

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2 flex-wrap">
            <Bot className="w-5 h-5 text-primary-500" />
            <h1 className="text-xl font-bold">Event Ops</h1>
            <span className="ml-2 text-[11px] text-dark-muted hidden md:inline">
              AI pipeline: Match → Re-opt → Reserve → Check → Audit
            </span>
          </div>
          <div className="flex items-center gap-2">
            <RefreshButton onClick={() => refetch()} isFetching={isFetching} />
            <Link
              href="/ops/conductor"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800"
            >
              <MessagesSquare className="w-4 h-4" />
              Conductor
            </Link>
            <Link
              href="/ops/activity"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800"
            >
              <ActivityIcon className="w-4 h-4" />
              Activity
            </Link>
            <Link
              href="/ops/no-shows"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800"
            >
              <UserX className="w-4 h-4" />
              No-shows
            </Link>
            <Link
              href="/ops/decisions"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800"
            >
              <Inbox className="w-4 h-4" />
              Decision inbox
              {pendingTotal > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                  {pendingTotal}
                </span>
              )}
            </Link>
          </div>
        </div>

        {isLoading && <p className="text-sm text-dark-muted">Loading ops overview…</p>}
        {error != null && <p className="text-sm text-red-600">Failed to load overview.</p>}
        {!isLoading && tiles.length === 0 && (
          <p className="text-sm text-dark-muted">
            No upcoming Outclose events with bookings in the next two weeks.
          </p>
        )}

        {visibleDates.map((date) => {
          const byEvent = byDate.get(date)!;
          const eventIds = [...byEvent.keys()].sort();
          return (
            <section key={date} className="mb-5">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">
                {formatDateHeader(date)}
              </h2>
              {eventIds.map((eventTimeId) => {
                const entries = byEvent.get(eventTimeId)!;
                return (
                  <div key={eventTimeId} className="mb-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap mb-1.5">
                      <span className="text-[11px] text-dark-muted">
                        {entries[0]?.tile.time}
                        {entries[0]?.tile.eventType === 'coffee' && ' · ☕ coffee (no reservations)'}
                        {' · '}
                        {entries.length} neighborhood
                        {entries.length !== 1 ? 's' : ''}
                      </span>
                      <span className="inline-flex items-center gap-2 flex-wrap">
                        <EventSweepButton eventTimeId={eventTimeId} />
                        {entries[0] && (
                          <EventStageChips
                            eventTimeId={eventTimeId}
                            anyNeighborhoodId={entries[0].n.neighborhoodId}
                            stages={entries[0].tile.eventStages}
                            onOpenRun={setOpenRunId}
                          />
                        )}
                        <BulkStageRunControl
                          eventTimeId={eventTimeId}
                          eventType={entries[0]?.tile.eventType}
                        />
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2">
                      {entries.map(({ tile, n }) => (
                        <ScopeTile
                          key={`${tile.eventTimeId}-${n.neighborhoodId}`}
                          tile={tile}
                          n={n}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </section>
          );
        })}

        {!showAll && dates.length > 3 && (
          <button
            onClick={() => setShowAll(true)}
            className="text-sm text-primary-500 font-medium hover:underline"
          >
            Show {dates.length - 3} more date{dates.length - 3 > 1 ? 's' : ''}
          </button>
        )}
      </div>
      <RunDetailDrawer runId={openRunId} onClose={() => setOpenRunId(null)} />
    </>
  );
}
